'use client';

import { IconButton, Tooltip, toasterApi } from '@terra/ui-design-system';
import { Disc2Icon } from '@terra/ui-icons';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSelectedTarget } from '../../store/projectStore';
import {
  StartTrackingDialog,
  type TrackingConfig,
} from '../dialogs/StartTrackingDialog';
import { useTracking } from '../../hooks/useTracking';
import { isApplicationActivated } from '../../utils/isApplicationActivated';

export function TopBarTrackingToggle() {
  const t = useTranslations('proxy.tracking');
  const [isStartDialogOpen, setIsStartDialogOpen] = useState(false);
  const selectedTarget = useSelectedTarget();
  const { isTrackingEnabled, startTracking, stopTracking } = useTracking();
  const handleToggleClick = async () => {
    if (isTrackingEnabled) {
      await handleStopTracking();

      return;
    }

    if (!selectedTarget?.id) {
      return;
    }

    if (!isApplicationActivated(selectedTarget)) {
      toasterApi.warning({
        title: t('applicationMustBeActivatedTitle'),
        description: t('applicationMustBeActivatedDescription'),
      });

      return;
    }

    setIsStartDialogOpen(true);
  };

  const handleStopTracking = async () => {
    if (!selectedTarget?.id) {
      console.error('No target selected');

      return;
    }

    const result = await stopTracking();

    if (!result.success) {
      toasterApi.error({
        title: 'Stop Tracking Failed',
        description:
          result.error || 'Failed to stop tracking. Please try again.',
      });
    }
  };

  const handleStartTracking = async (config: TrackingConfig) => {
    const result = await startTracking(config);

    if (!result.success) {
      console.error('Failed to start tracking:', result.error);
    } else {
      setIsStartDialogOpen(false);
    }
  };

  return (
    <>
      <Tooltip
        title={isTrackingEnabled ? 'Disable tracking' : 'Configure tracking'}
      >
        <IconButton
          variant="ghost"
          size="xs"
          aria-label="Toggle request tracking"
          onClick={handleToggleClick}
          disabled={!selectedTarget?.id}
        >
          <Disc2Icon
            size={16}
            style={{
              color: isTrackingEnabled ? 'red' : undefined,
            }}
          />
        </IconButton>
      </Tooltip>

      <StartTrackingDialog
        isOpen={isStartDialogOpen}
        onOpenChange={setIsStartDialogOpen}
        onStartTracking={handleStartTracking}
      />
    </>
  );
}
